import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, Box, IconButton } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { X } from 'react-feather'
import { ModalLayout } from '@/components'
import {
  AutocompleteOption,
  ControlledAutocomplete,
  ControlledCheckboxField,
  ControlledDateField,
  ControlledSelect,
  ProjectsMultiSelect,
  ResearchersMultiSelect,
} from '@/components/inputs'
import { useHoursReportsExport, useUsersContractors } from '@/hooks'
import * as yup from 'yup'
import dayjs from 'dayjs'
import { theme } from '@/theme'
import { DATE_FORMAT_YMD } from '@/constants'
import {
  ExportType,
  EXPORT_TYPE_OPTIONS,
} from '@/hooks/api/hoursReports/useHoursReportsExport'

type HoursReportsExportFormData = {
  contractor: AutocompleteOption | null
  researchers: string | null
  project: string | null
  startDate: string | null
  endDate: string | null
  isExported: boolean | null
  exportType: ExportType
  includeManualHours: boolean
  includeExpenseDeclarations: boolean
}

const exportSchema = yup.object({
  contractor: yup
    .object({
      value: yup.string(),
      title: yup.string(),
    })
    .typeError('required')
    .required(),
  researchers: yup.string().optional().nullable(),
  project: yup.string().optional().nullable(),
  startDate: yup.date().typeError('invalid_date').required(),
  endDate: yup.date().typeError('invalid_date').optional().nullable(),
  isExported: yup.boolean().optional(),
  exportType: yup
    .string()
    .oneOf([
      EXPORT_TYPE_OPTIONS.EXTERNAL,
      EXPORT_TYPE_OPTIONS.INTERNAL_WITH_DISTANCE,
    ])
    .required(),
  includeManualHours: yup.boolean().optional(),
  includeExpenseDeclarations: yup.boolean().optional(),
})

export const HoursReportsExportForm = (): JSX.Element => {
  const { t } = useTranslation('hoursReporting')

  const { enqueueSnackbar } = useSnackbar()

  const { exportReport, loading: isExportInProcess } = useHoursReportsExport()

  const { data: contractors } = useUsersContractors()

  const options = useMemo(
    () =>
      contractors
        ?.sort((a, b) => (a.name < b.name ? -1 : 1))
        .map(({ id, name }) => ({
          title: name,
          value: id.toString(),
        })) || [],
    [contractors]
  )

  const exportTypeOptions = useMemo(
    () => [
      {
        name: t('exportTypes.external'),
        value: EXPORT_TYPE_OPTIONS.EXTERNAL,
      },
      {
        name: t('exportTypes.internalWithDistance'),
        value: EXPORT_TYPE_OPTIONS.INTERNAL_WITH_DISTANCE,
      },
    ],
    [t]
  )

  const defaultValues = {
    contractor: null,
    researchers: null,
    project: null,
    startDate: null,
    endDate: null,
    isExported: false,
    exportType: EXPORT_TYPE_OPTIONS.EXTERNAL,
    includeManualHours: false,
    includeExpenseDeclarations: false,
  }

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<HoursReportsExportFormData>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(exportSchema),
  })

  const handleReset = () =>
    reset({
      ...defaultValues,
    })

  const handleExportSubmit = async ({
    contractor,
    researchers,
    project,
    startDate,
    endDate,
    isExported,
    exportType,
    includeManualHours,
    includeExpenseDeclarations,
  }: HoursReportsExportFormData) => {
    const exportData = {
      contractor: contractor?.value,
      researchers: researchers || undefined,
      project: project || undefined,
      startDate: startDate
        ? dayjs(startDate).format(DATE_FORMAT_YMD)
        : undefined,
      endDate: endDate ? dayjs(endDate).format(DATE_FORMAT_YMD) : undefined,
      isExported: isExported === null ? undefined : isExported,
      exportType,
      includeManualHours: includeManualHours || undefined,
      includeExpenseDeclarations: includeExpenseDeclarations || undefined,
    }

    try {
      await exportReport(exportData)

      enqueueSnackbar(t('titles.exportedSuccessfully'), {
        variant: 'success',
      })

      handleReset()
    } catch (e) {
      enqueueSnackbar(`${t('errors:api.execute')}: ${e}`, {
        variant: 'error',
      })
    }
  }

  return (
    <ModalLayout title={t('titles.exportHoursReports')}>
      <form onSubmit={handleSubmit(async (data) => handleExportSubmit(data))}>
        <Stack direction="row">
          <Stack direction="column" spacing={4}>
            <Stack direction="row" spacing={4}>
              <Box width={300}>
                <ControlledAutocomplete
                  name="contractor"
                  options={options}
                  control={control}
                  textFieldProps={{
                    placeholder: t('placeholders.contractor'),
                  }}
                  multiple={false}
                  fullWidth={true}
                />
              </Box>
              <ControlledCheckboxField
                name="isExported"
                control={control}
                label={t('forms:placeholders.isExported')}
              />
              <ControlledCheckboxField
                name="includeManualHours"
                control={control}
                label={t('labels.includeManualHours')}
              />
              <ControlledCheckboxField
                name="includeExpenseDeclarations"
                control={control}
                label={t('labels.includeExpenseDeclarations')}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="flex-start"
              spacing={2}
            >
              <ControlledDateField
                name="startDate"
                placeholder={t('forms:placeholders.start-date')}
                control={control}
                apiFieldName="startDate"
              />
              <ControlledDateField
                name="endDate"
                placeholder={t('forms:placeholders.end-date')}
                control={control}
                apiFieldName="endDate"
              />
              <Box width={300}>
                <ControlledSelect
                  name="exportType"
                  control={control}
                  options={exportTypeOptions}
                  label={t('labels.exportType')}
                  fullWidth={true}
                />
              </Box>
            </Stack>

            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="flex-start"
              spacing={2}
            >
              <Controller
                name="researchers"
                control={control}
                render={({ field }) => (
                  <ResearchersMultiSelect
                    value={field.value as string}
                    onChange={field.onChange}
                    placeholder={t('placeholders.researchers')}
                  />
                )}
              />
              <Controller
                name="project"
                control={control}
                render={({ field }) => (
                  <ProjectsMultiSelect
                    value={field.value as string}
                    onChange={field.onChange}
                    placeholder={t('placeholders.projects')}
                  />
                )}
              />
            </Stack>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={5}>
            <Box mt={1} ml={4}>
              <IconButton
                onClick={handleReset}
                title={t('buttons.resetFilters')}
              >
                <X size="16px" color={theme.palette.green['500']} />
              </IconButton>
            </Box>
            <LoadingButton
              variant="contained"
              color="secondary"
              type="submit"
              loading={isExportInProcess}
              disableElevation={true}
              disabled={!isDirty}
            >
              <span> {t('buttons.export')}</span>
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
    </ModalLayout>
  )
}
